// Global imports
import { useState } from 'react'
import { useRouteLoaderData } from '@remix-run/react'
import { clsx } from 'clsx'
import {
  checkNumberOfDays,
  countInclusiveDays,
  resolveHirePeriodTooltip,
} from '~/utils'
import { chooseBrendaHireEndDate } from '~/utils/brendaDates'
import {
  Button,
  Price,
  QuantitySelector,
  AddressFinder,
  HssLoader,
  FulfilmentButtons,
  ProductDeliveryTimer,
  DateInput,
  HelpPopover,
} from '~/components'
import { useProductData } from '~/hooks'
import { useProductSingleContext } from '~/context/productSingle'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

// Types
import type { RootDataProps } from '~/types'

interface Props {
  /** `'bulk'` = hire, anything else = consumable/buy. */
  productType: string
  /** Stock code for the currently active variant. */
  stockCode: string
  /** Display title for the active variant. */
  title?: string
  /** Variant option pair (e.g. `{ name: 'Size', value: '9.5M' }`). */
  option?: { name: string; value: string }
  /** Whether Saturday should be allowed on the date picker. */
  allowSaturday?: boolean
}

const ErrorMessage = ({
  message,
  marginBottom,
}: {
  message?: string
  marginBottom?: boolean
}) => {
  if (!message) return null

  return (
    <p
      className={clsx('flex text-red-500', {
        'mb-5': marginBottom,
        'mb-0': !marginBottom,
      })}
    >
      <ExclamationCircleIcon className="size-5 shrink-0" />
      &nbsp;
      <span className="pt-0.5 text-xs">{message}</span>
    </p>
  )
}

/**
 * AddToBasket — the PDP's hire/buy control block.
 *
 * All state lives in `useProductSingleContext()`; this component reads
 * from the context and renders the UI. The only props it still takes
 * are the ones that come from the product data itself (productType,
 * stockCode, title, allowSaturday) — everything else (address, charges
 * result, basket flow, bank holidays, etc.) is read from context.
 */
const AddToBasket = ({
  productType,
  stockCode,
  title,
  option,
  allowSaturday = false,
}: Props) => {
  const isHire = productType === 'bulk'

  const { site } = useRouteLoaderData('root') as RootDataProps

  const {
    fromPrice,
    salePrice,
    averageHire,
    address,
    addressData,
    fulfilment,
    userSelectedBranchId,
    setAddress,
    setAddressData,
    setFulfilment,
    setPickupModalOpen,
    charges,
    chargesLoading,
    chargesError,
    bankHolidays,
    submitCharges,
    resetPricing,

    basketLoading,
    setBasketProduct,
  } = useProductSingleContext()

  // Inline error for validation failures
  const [inlineError, setInlineError] = useState<string | null>(null)

  // Product data hook — `chargesError` is passed straight through as
  // `pricesError`; the date hook accepts the `BrendaChargesErrorState`
  // shape directly so no adapter is needed.
  const {
    quantity,
    price,
    priceSale,
    startDate,
    endDate,
    productError,
    setQuantity,
    buildBasketItem,
    buildProductChangeRequest,
    handleStartDateChange,
    handleEndDateChange,
    handleBuyDeliveryDateChange,
  } = useProductData({
    fromPrice,
    salePrice,
    fulfilment,
    pricesError: chargesError,
    buyProduct: !isHire,
    averageHire: isHire ? averageHire : undefined,
    charges,
    setFulfilment,
    allowSaturday,
  })

  // Hire length as Brenda charges it — the start and end day both count,
  // so the 15th to the 16th is a 2 day hire
  const hireDays = countInclusiveDays(startDate, endDate)

  // Article for the spoken duration — "an 8 day", "an 11 day", "a 14 day"
  const hirePeriodArticle =
    /^8/.test(String(hireDays)) || [11, 18].includes(hireDays) ? 'an' : 'a'

  // Selected hire duration, shown under the date fields and inside both
  // calendars so the length of the hire stays visible while one is open
  const hirePeriodMessage =
    startDate && endDate ? (
      <span>
        You have selected {hirePeriodArticle}{' '}
        <span className="font-semibold">{hireDays} day</span> hire
      </span>
    ) : null

  // CMS-driven explanation of the selected hire length — the band whose day
  // range contains hireDays, or none when the CMS has no matching band
  const hirePeriodTooltip = resolveHirePeriodTooltip(
    site?.global?.hirePeriodTooltips,
    hireDays
  )

  // Add item to basket
  const handleAdd = () => {
    setInlineError(null)
    const mode = isHire ? 'hire' : 'buy'
    const res = buildBasketItem({ mode, charges, address, title, option })

    if (!res.ok) {
      setInlineError(res.message)
      return
    }

    setBasketProduct(res.data)
  }

  // Handle quantity change — re-pins the user's chosen pickup branch
  // (non-strict) so the new quantity is checked against it, falling
  // back to the nearest viable branch if it can't fulfil the amount.
  const handleQuantityChange = (value: string) => {
    setInlineError(null)
    const req = buildProductChangeRequest({
      stockCode,
      address,
      addressData,
      quantity: value,
      currentBranchId: userSelectedBranchId,
    })

    if (!req.ok) {
      setInlineError(req.message)
      return
    }

    submitCharges(req.data)
  }

  // Handle fulfilment change on PDP — triggers a re-fetch with the
  // fulfilment-specific start date so the price matches the chosen
  // option. `useBrendaCharges` will shift-detect on the response if the
  // new side needs a later window.
  const handleFulfilmentChange = (next: string | null) => {
    if (!next || !isHire || !charges) {
      setFulfilment(next)
      return
    }

    setFulfilment(next)

    // Determine the start date for the new fulfilment — reads from the
    // chosen side's resolved date on the current charges result.
    const newStart =
      next === 'pickup' ? charges.pickup.date : charges.delivery.date

    // Only re-fetch if the start date differs from the current fetch
    if (newStart && newStart !== charges.request.resolvedStartDate) {
      // Always derive duration from the user's *originally-requested*
      // range — NOT the resolved window, which may have been BH-bumped
      // on a previous request and would otherwise silently stretch the
      // hire period on each fulfilment switch.
      const duration = checkNumberOfDays(
        charges.request.originalStartDate,
        charges.request.originalEndDate
      )
      const newEnd = chooseBrendaHireEndDate(duration, newStart, bankHolidays)

      const req = buildProductChangeRequest({
        stockCode,
        address,
        addressData,
        quantity,
      })

      if (req.ok) {
        const hasBranchSelection = userSelectedBranchId != null

        // Preserve the side we're switching *away from* so its resolved
        // date survives the re-fetch. Exception: when the user has
        // chosen a pickup branch we re-pin it via `currentBranchId`
        // instead (below), so we must NOT preserve the stale pickup
        // side — that would overwrite the freshly re-pinned branch with
        // the default nearest one.
        const preserveSide =
          next === 'pickup' ? 'delivery' : hasBranchSelection ? null : 'pickup'

        submitCharges(
          {
            ...req.data,
            startDate: newStart,
            endDate: newEnd,
            // The user explicitly picked a side — tell the hook so
            // `resolvedStartDate` tracks it and any correction
            // re-fetch anchors on this side rather than whichever
            // happens to be earliest.
            selectedFulfilment: next as 'delivery' | 'pickup',
            // Re-pin the user's chosen branch (non-strict) on *either*
            // direction so the pickup side always reflects their
            // selection, even while delivery is active.
            ...(hasBranchSelection && {
              currentBranchId: userSelectedBranchId,
            }),
          },
          // Keep the section on screen during every fulfilment switch
          // (spinner, not a full reload). When we can't preserve a side
          // — switching to delivery with a re-pinned branch — fall back
          // to `keepResult` so it still doesn't blank out.
          preserveSide ? { preserveSide } : { keepResult: true }
        )
      }
    }
  }

  // Price check for hire vs buy — true once we have a resolved charges
  // result with an actual price (not the from-price fallback).
  const hasPrice = !!charges?.product?.price

  // The system silently moved the user's chosen pickup branch — e.g. a
  // quantity change pushed it past that branch's stock, so it fell back
  // to the nearest available branch (and likely a different date). Flag
  // it so the change doesn't pass unnoticed.
  //
  // Gated on `!chargesLoading`: a modal branch-selection updates
  // `userSelectedBranchId` immediately while the kept-on-screen result
  // still holds the previous branch, so this would briefly mis-fire
  // mid-fetch. Once the fetch settles a deliberate selection resolves to
  // the chosen branch (no mismatch); only a fallback leaves them apart.
  const pickupBranchMoved =
    isHire &&
    !chargesLoading &&
    fulfilment === 'pickup' &&
    userSelectedBranchId != null &&
    charges?.pickup.branchId != null &&
    charges.pickup.branchId !== userSelectedBranchId

  return (
    <>
      <div className="relative mb-5 rounded-2xl bg-neutral-150 p-5">
        <div
          className={clsx('transition-opacity', {
            'pointer-events-none opacity-50': chargesLoading,
          })}
        >
          <div className="mb-5">
            <p className="mb-2 text-sm font-semibold">
              {hasPrice
                ? isHire
                  ? 'Hire now'
                  : 'Buy now'
                : isHire
                  ? 'Hire from'
                  : 'Buy from'}
            </p>
            {price ? (
              <Price
                size="xl"
                price={price}
                salePrice={priceSale}
                highlight={hasPrice}
                hasExcluding
              />
            ) : (
              <p>Enter address to see prices</p>
            )}
          </div>
          {isHire ? (
            <>
              {averageHire ? (
                <p className="mb-2 text-sm">
                  Average hire period:&nbsp;
                  <span className="font-semibold">{averageHire}&nbsp;days</span>
                </p>
              ) : null}
              <div className="mb-5">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <DateInput
                    id="prices-start-date"
                    label="Start:"
                    labelInset={64}
                    value={startDate}
                    allowSaturday={allowSaturday}
                    footer={hirePeriodMessage}
                    onChange={value =>
                      handleStartDateChange({
                        value,
                        resetQuantity: () => setQuantity('1'),
                        clearProductData: resetPricing,
                      })
                    }
                  />
                  <DateInput
                    id="prices-end-date"
                    label="End:"
                    labelInset={58}
                    value={endDate}
                    allowSaturday={allowSaturday}
                    footer={hirePeriodMessage}
                    onChange={value =>
                      handleEndDateChange({
                        value,
                        resetQuantity: () => setQuantity('1'),
                        clearProductData: resetPricing,
                      })
                    }
                  />
                </div>
                {hirePeriodMessage && (
                  <div className="mt-3 flex items-start gap-1.5 text-sm">
                    <p className="mb-0">{hirePeriodMessage}</p>
                    {hirePeriodTooltip && (
                      <HelpPopover
                        title={hirePeriodTooltip.title}
                        description={hirePeriodTooltip.content}
                        size="xs"
                      />
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="mb-4">
              <p className="mb-2 block text-sm font-medium">
                When would you like this delivered?
              </p>
              <DateInput
                id="field-delivery-date"
                label="Select date:"
                labelInset={120}
                value={startDate}
                allowSaturday={allowSaturday}
                onChange={value =>
                  handleBuyDeliveryDateChange({
                    value,
                    resetQuantity: () => setQuantity('1'),
                    clearProductData: resetPricing,
                  })
                }
              />
            </div>
          )}
          {(productError?.message || inlineError) && (
            <ErrorMessage
              message={inlineError ?? productError?.message}
              marginBottom
            />
          )}
          <div className="mb-5">
            <div className="flex flex-col gap-3 md:flex-row">
              <div className="flex-1">
                <AddressFinder
                  label={addressData ? 'Your address' : 'Check availability'}
                  labelSize="sm"
                  address={address}
                  addressData={addressData}
                  disabled={!!charges}
                  setAddress={setAddress}
                  setAddressData={setAddressData}
                  onChangeAddress={() => {
                    setQuantity('1')
                    resetPricing()
                  }}
                  dynamicWidth
                />
              </div>
              <div
                className={clsx('shrink-0 md:w-44 md:pt-7', {
                  'hidden md:inline-block': !!charges,
                })}
              >
                <Button
                  title={
                    chargesLoading
                      ? 'Fetching'
                      : isHire
                        ? 'Get price'
                        : 'Get availability'
                  }
                  onClick={() => {
                    const req = buildProductChangeRequest({
                      stockCode,
                      address,
                      addressData,
                      quantity,
                    })

                    if (!req.ok) {
                      setInlineError('Failed to fetch product data')
                      return
                    }

                    submitCharges(req.data)
                  }}
                  disabled={
                    !!productError?.message ||
                    chargesLoading ||
                    !!charges ||
                    !addressData
                  }
                  fullWidth
                />
              </div>
            </div>
          </div>
          <div
            className={clsx('mb-3 flex flex-col gap-4', {
              hidden: !charges,
            })}
          >
            {charges && (
              <FulfilmentButtons
                address={address}
                delivery={charges.delivery}
                pickup={charges.pickup}
                fulfilment={fulfilment}
                setFulfilment={isHire ? handleFulfilmentChange : undefined}
                deliveryOnly={!isHire}
                unavailableDeliveryDate={
                  !isHire && startDate !== charges.delivery.date
                    ? startDate
                    : null
                }
                onChangeBranch={
                  isHire ? () => setPickupModalOpen(true) : undefined
                }
              />
            )}
            {pickupBranchMoved && (
              <ErrorMessage message="Your pickup branch has been changed due to an earlier date being available, or a lack of stock to fulfill your requested quantity." />
            )}
            <div>
              <ProductDeliveryTimer
                deliveryDate={charges?.delivery?.date ?? null}
              />
            </div>
            <div className="flex flex-col gap-3 xs:flex-row">
              <div>
                <QuantitySelector
                  min={1}
                  max={isHire ? 10 : 1000}
                  value={quantity}
                  setValue={setQuantity}
                  onChange={handleQuantityChange}
                  disabled={chargesLoading || !charges}
                />
              </div>
              <div className="flex-1">
                <Button
                  title={basketLoading ? 'Adding..' : 'Add to basket'}
                  onClick={handleAdd}
                  disabled={chargesLoading || !charges || basketLoading}
                  fullWidth
                />
              </div>
            </div>
          </div>
        </div>
        {chargesLoading && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <HssLoader size="md" />
          </div>
        )}
      </div>
    </>
  )
}

export default AddToBasket
